import { Capacitor } from '@capacitor/core';
import 'cordova-plugin-android-permissions/www/permissions';

const NATIVE_PLATFORMS = new Set(['android', 'ios']);

const isNativePlatform = () => {
  if (typeof Capacitor?.getPlatform === 'function') {
    return NATIVE_PLATFORMS.has(Capacitor.getPlatform());
  }
  return typeof window !== 'undefined' && window.cordova !== undefined;
};

let cordovaPermissionsPromise;

function waitForCordovaPermissions() {
  if (cordovaPermissionsPromise) {
    return cordovaPermissionsPromise;
  }

  cordovaPermissionsPromise = new Promise((resolve) => {
    const resolveWithCurrent = () => resolve(window.cordova?.plugins?.permissions || null);

    if (window.cordova?.plugins?.permissions) {
      resolveWithCurrent();
      return;
    }

    const handler = () => {
      document.removeEventListener('deviceready', handler);
      resolveWithCurrent();
    };

    document.addEventListener('deviceready', handler, { once: true });
  });

  return cordovaPermissionsPromise;
}

function normalizePermissionsObject(perms) {
  if (!perms) {
    if (window.plugins?.permissions) {
      return window.plugins.permissions;
    }
    return null;
  }

  if (typeof perms.checkPermission === 'function') {
    return perms;
  }

  if (perms.androidPermissions) {
    return perms.androidPermissions;
  }

  return null;
}

export async function requestMicPermission() {
  if (!isNativePlatform()) {
    console.log('[BlueWire] requestMicPermission: non-native platform, auto-allow');
    return true;
  }

  const permissions = normalizePermissionsObject(await waitForCordovaPermissions());
  if (!permissions) {
    console.error('Cordova permissions plugin not available');
    return false;
  }

  const requiredPermissions = [permissions.RECORD_AUDIO].filter(Boolean);
  if (permissions.MODIFY_AUDIO_SETTINGS) {
    requiredPermissions.push(permissions.MODIFY_AUDIO_SETTINGS);
  }

  return new Promise((resolve) => {
    console.log('[BlueWire] requestMicPermission: checking native mic permission...');
    permissions.checkPermission(
      permissions.RECORD_AUDIO,
      (status) => {
        console.log('[BlueWire] requestMicPermission: current status', status);
        if (status?.hasPermission) {
          console.log('[BlueWire] requestMicPermission: already granted');
          resolve(true);
          return;
        }

        console.log('[BlueWire] requestMicPermission: requesting permissions');
        permissions.requestPermissions(
          requiredPermissions,
          (result) => {
            console.log('[BlueWire] requestMicPermission: grant result', result);
            resolve(!!result?.hasPermission);
          },
          () => {
            console.warn('[BlueWire] requestMicPermission: requestPermissions failed');
            resolve(false);
          }
        );
      },
      () => {
        console.warn('[BlueWire] requestMicPermission: checkPermission failed');
        resolve(false);
      }
    );
  });
}
